import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function AdminLoginPage() {
  const { user, loading, isAdmin, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && user && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const { error: err } = await signIn(email, password);
      if (err) setError(err);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center font-heading font-bold">
              TG
            </span>
            <div className="text-left">
              <div className="font-heading font-bold text-xl text-foreground-900">TGMCI Admin</div>
              <div className="text-xs text-foreground-500">Content Management Dashboard</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-background-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                placeholder="admin@tgmci.org"
              />
            </div>

            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                placeholder="••••••••"
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/admin/forgot-password"
                className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <div className="px-3 py-2.5 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
              {submitting ? 'Please wait...' : 'Sign In'}
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
            <i className="ri-arrow-left-line mr-1" aria-hidden="true" />
            Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}